import React, { useMemo } from 'react';
import { X, MessageSquare } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Label,
} from 'recharts';
import './MessageAnalyticsModal.css';

const TEAL  = '#1D9E75';
const BLUE  = '#185FA5';
const RED   = '#E24B4A';
const AMBER = '#EF9F27';
const GRAY  = '#94a3b8';

const MsgTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="msg-tt">
      {label && <p className="msg-tt-label">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="msg-tt-row">
          <span className="msg-tt-dot" style={{ background: p.fill || p.color }} />
          <span>{p.name}: <strong>{(p.value ?? 0).toLocaleString()}</strong></span>
        </div>
      ))}
    </div>
  );
};

const PERIOD_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
};

const MessageAnalyticsModal = ({ data, loading, onClose }) => {
  const funnel   = data?.deliveryFunnel        || {};
  const periods  = data?.periodBreakdown       || {};
  const failures = data?.failureAnalysis       || {};
  const cats     = data?.templateCategoryPerformance || [];
  const daily    = data?.dailyTrend            || [];

  /* Donut: delivered / seen / failed / pending */
  const pieData = useMemo(() => [
    { name: 'Delivered', value: funnel.delivered || 0, color: TEAL  },
    { name: 'Seen',      value: funnel.seen      || 0, color: BLUE  },
    { name: 'Failed',    value: funnel.failed    || 0, color: RED   },
    { name: 'Pending',   value: funnel.pending   || 0, color: AMBER },
  ].filter(d => d.value > 0), [funnel]);

  const pieTotal = funnel.totalMessages || 0;

  /* Daily trend bar data */
  const trendData = useMemo(() =>
    daily.map(d => ({
      name:      d.dateLabel?.split(' ').slice(0, 2).join(' ') || d.date,
      Delivered: d.delivered,
      Seen:      d.seen,
      Failed:    d.failed,
    })),
  [daily]);

  const hasTrend = trendData.some(d => d.Delivered > 0 || d.Seen > 0 || d.Failed > 0);

  /* Period cards list */
  const periodList = Object.entries(PERIOD_LABELS).map(([key, label]) => ({
    key, label, ...(periods[key] || {}),
  }));

  return (
    <div className="msg-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="msg-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="msg-hdr">
          <div className="msg-hdr-left">
            <div className="msg-hdr-badge"><MessageSquare size={16} /></div>
            <div>
              <h2 className="msg-hdr-title">Message Analytics</h2>
              <p className="msg-hdr-sub">Delivery funnel &amp; performance insights</p>
            </div>
          </div>
          <button className="msg-hdr-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {/* ── Body ── */}
        {loading ? (
          <div className="msg-loading">
            <div className="msg-spin" />
            <p>Loading analytics…</p>
          </div>
        ) : (
          <div className="msg-body">

            {/* Section 1 — Delivery Funnel stats */}
            <div className="msg-section">
              <p className="msg-section-label">Delivery Funnel</p>
              <div className="msg-stat-grid">
                {[
                  { label: 'Total',     value: funnel.totalMessages || 0, accent: null  },
                  { label: 'Delivered', value: funnel.delivered     || 0, accent: TEAL  },
                  { label: 'Seen',      value: funnel.seen          || 0, accent: BLUE  },
                  { label: 'Failed',    value: funnel.failed        || 0, accent: RED   },
                  { label: 'Pending',   value: funnel.pending       || 0, accent: AMBER },
                  { label: 'Sent',      value: funnel.sent          || 0, accent: null  },
                ].map(s => (
                  <div key={s.label} className="msg-stat-card">
                    <span className="msg-stat-val" style={s.accent ? { color: s.accent } : {}}>{s.value.toLocaleString()}</span>
                    <span className="msg-stat-lbl">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 — Performance rates */}
            <div className="msg-section">
              <p className="msg-section-label">Performance Rates</p>
              <div className="msg-perf-grid">
                {[
                  { label: 'Delivery Rate', value: `${funnel.deliveryRate ?? 0}%`, color: TEAL },
                  { label: 'Read Rate',     value: `${funnel.readRate    ?? 0}%`, color: BLUE },
                  { label: 'Failure Rate',  value: `${funnel.failureRate ?? 0}%`, color: RED  },
                ].map(r => (
                  <div key={r.label} className="msg-perf-card">
                    <span className="msg-perf-val" style={{ color: r.color }}>{r.value}</span>
                    <span className="msg-perf-lbl">{r.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 — Donut + horizontal bars */}
            <div className="msg-section">
              <p className="msg-section-label">Message Breakdown</p>
              <div className="msg-breakdown-row">

                {/* Donut */}
                <div className="msg-card msg-donut-card">
                  <ResponsiveContainer width="100%" height={190}>
                    <PieChart>
                      <Pie
                        data={pieData.length ? pieData : [{ name: 'No data', value: 1, color: '#e2e8f0' }]}
                        cx="50%" cy="50%"
                        innerRadius={52} outerRadius={78}
                        paddingAngle={pieData.length > 1 ? 2 : 0}
                        dataKey="value" stroke="none"
                        startAngle={90} endAngle={-270}
                      >
                        {(pieData.length ? pieData : [{ color: '#e2e8f0' }]).map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                        <Label content={({ viewBox }) => {
                          const { cx, cy } = viewBox;
                          return (
                            <g>
                              <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle"
                                style={{ fontSize: 20, fontWeight: 500, fill: 'var(--msg-text)', fontFamily: 'inherit' }}>
                                {pieTotal.toLocaleString()}
                              </text>
                              <text x={cx} y={cy + 16} textAnchor="middle" dominantBaseline="middle"
                                style={{ fontSize: 10, fill: 'var(--msg-muted)', fontFamily: 'inherit' }}>
                                total
                              </text>
                            </g>
                          );
                        }} />
                      </Pie>
                      <Tooltip content={<MsgTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="msg-donut-legend">
                    {[
                      { label: 'Delivered', color: TEAL  },
                      { label: 'Seen',      color: BLUE  },
                      { label: 'Failed',    color: RED   },
                      { label: 'Pending',   color: AMBER },
                    ].map(l => (
                      <span key={l.label} className="msg-legend-item">
                        <span className="msg-legend-dot" style={{ background: l.color }} />
                        {l.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Horizontal count bars */}
                <div className="msg-card msg-hbar-card">
                  <p className="msg-card-title">Message Counts</p>
                  <div className="msg-hbars">
                    {[
                      { name: 'Delivered', value: funnel.delivered || 0, color: TEAL  },
                      { name: 'Seen',      value: funnel.seen      || 0, color: BLUE  },
                      { name: 'Failed',    value: funnel.failed    || 0, color: RED   },
                      { name: 'Pending',   value: funnel.pending   || 0, color: AMBER },
                      { name: 'Sent',      value: funnel.sent      || 0, color: GRAY  },
                    ].map(item => {
                      const sum = pieTotal || 1;
                      const pct = Math.round((item.value / sum) * 100);
                      return (
                        <div key={item.name} className="msg-hbar-row">
                          <span className="msg-hbar-lbl">{item.name}</span>
                          <div className="msg-hbar-track">
                            <div className="msg-hbar-fill" style={{ width: `${pct}%`, background: item.color }} />
                          </div>
                          <span className="msg-hbar-val">{item.value.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            </div>

            {/* Section 4 — Daily Trend */}
            {hasTrend && (
              <div className="msg-section">
                <p className="msg-section-label">Daily Trend (Last 7 Days)</p>
                <div className="msg-card msg-trend-card">
                  <div className="msg-chart-legend">
                    <span className="msg-legend-item"><span className="msg-legend-dot" style={{ background: TEAL }} />Delivered</span>
                    <span className="msg-legend-item"><span className="msg-legend-dot" style={{ background: BLUE }} />Seen</span>
                    <span className="msg-legend-item"><span className="msg-legend-dot" style={{ background: RED  }} />Failed</span>
                  </div>
                  <ResponsiveContainer width="100%" height={175}>
                    <BarChart data={trendData} barSize={12} barGap={2} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--msg-grid)" />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--msg-muted)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'var(--msg-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<MsgTooltip />} cursor={{ fill: 'var(--msg-hover)' }} />
                      <Bar dataKey="Delivered" fill={TEAL} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Seen"      fill={BLUE} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Failed"    fill={RED}  radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Section 5 — Period Breakdown */}
            <div className="msg-section">
              <p className="msg-section-label">Period Breakdown</p>
              <div className="msg-period-grid">
                {periodList.map(p => (
                  <div key={p.key} className="msg-period-card">
                    <p className="msg-period-label">{p.label}</p>
                    <div className="msg-period-rows">
                      <span className="msg-period-row"><span className="msg-period-dot" style={{ background: TEAL }} />Del <strong style={{ color: TEAL }}>{(p.delivered || 0).toLocaleString()}</strong></span>
                      <span className="msg-period-row"><span className="msg-period-dot" style={{ background: BLUE }} />Seen <strong style={{ color: BLUE }}>{(p.seen || 0).toLocaleString()}</strong></span>
                      <span className="msg-period-row"><span className="msg-period-dot" style={{ background: RED  }} />Fail <strong style={{ color: RED  }}>{(p.failed || 0).toLocaleString()}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 6 — Template Category Performance */}
            {cats.length > 0 && (
              <div className="msg-section">
                <p className="msg-section-label">Template Category Performance</p>
                <div className="msg-table-wrap">
                  <table className="msg-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Sent</th>
                        <th style={{ color: TEAL }}>Delivered</th>
                        <th style={{ color: BLUE }}>Seen</th>
                        <th style={{ color: RED  }}>Failed</th>
                        <th>Del. Rate</th>
                        <th>Read Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cats.map(c => (
                        <tr key={c.category}>
                          <td>
                            <span className={`msg-type-pill ${c.category === 'utility' ? 'msg-pill-teal' : 'msg-pill-amber'}`}>
                              {c.category}
                            </span>
                          </td>
                          <td>{c.totalSent}</td>
                          <td style={{ color: TEAL, fontWeight: 500 }}>{c.delivered}</td>
                          <td style={{ color: BLUE, fontWeight: 500 }}>{c.seen}</td>
                          <td style={{ color: RED,  fontWeight: 500 }}>{c.failed}</td>
                          <td>
                            <div className="msg-mini-bar">
                              <div className="msg-mini-track"><div className="msg-mini-fill" style={{ width: `${Math.min(c.deliveryRate, 100)}%`, background: TEAL }} /></div>
                              <span className="msg-mini-pct">{c.deliveryRate}%</span>
                            </div>
                          </td>
                          <td>
                            <div className="msg-mini-bar">
                              <div className="msg-mini-track"><div className="msg-mini-fill" style={{ width: `${Math.min(c.readRate, 100)}%`, background: BLUE }} /></div>
                              <span className="msg-mini-pct">{c.readRate}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section 7 — Failure Analysis */}
            {(failures.totalFailed > 0 || failures.byErrorCode?.length > 0) && (
              <div className="msg-section">
                <p className="msg-section-label">
                  Failure Analysis
                  <span className="msg-fail-total">{failures.totalFailed} total failed</span>
                </p>
                <div className="msg-table-wrap">
                  <table className="msg-table">
                    <thead>
                      <tr>
                        <th>Error Code</th>
                        <th>Message</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(failures.byErrorCode || []).map((e, i) => (
                        <tr key={i}>
                          <td><span className="msg-error-code">{e.errorCode}</span></td>
                          <td className="msg-error-msg">{e.errorMessage}</td>
                          <td><span style={{ color: RED, fontWeight: 500 }}>{e.count}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default MessageAnalyticsModal;
